import React, { useRef, useEffect, useState, useContext } from "react";
import { envelope, featureCollection } from "@turf/turf";
import { startOfMonth, endOfMonth, format } from "date-fns";
import { useParams } from "react-router-dom";
// eslint-disable-next-line
import mapboxgl from "!mapbox-gl";
import geoViewport from "@mapbox/geo-viewport";
import { CheckCircle, Cloud, CloudSun, Stack, Sun } from "phosphor-react";
import { UserContext } from "context/user";
import {
  StackAnalysisContext,
  StackAnalysisActions,
} from "context/stack_analysis";
import { useFieldsQuery } from "hooks/useFields";
import { useCatalogQuery } from "hooks/useCatalog";
import Button from "components/Button";
import Loading from "components/Loading";
import "./index.scss";

const StackCapturesSelection = () => {
  const mapContainer = useRef(null);
  const { token } = useContext(UserContext);
  const { state, dispatch } = useContext(StackAnalysisContext);
  const { selectedYears, selectedMonth, selectedFields, selectedDates } = state;
  const { farmId } = useParams();
  const [map, setMap] = useState(null);
  const [currentYear, setCurrentYear] = useState(selectedYears[0]);
  const [mapFullyLoaded, setMapFullyLoaded] = useState(false);
  const { isLoading: isLoadingFields, data: fieldsData } =
    useFieldsQuery(farmId);
  const monthDate = new Date(
    Date.parse(`${selectedMonth} 1, ${currentYear}`)
  ).getMonth();
  const startDate = startOfMonth(new Date(currentYear, monthDate, 1));
  const endDate = endOfMonth(new Date(currentYear, monthDate, 1));
  const startDateStr = format(startDate, "yyyy-MM-dd");
  const endDateStr = format(endDate, "yyyy-MM-dd");
  const startDateQuery = `&date_start=${startDateStr}`;
  const endDateQuery = `&date_end=${endDateStr}`;
  const fields = selectedFields.map((id) =>
    fieldsData.features.find((field) => field.id === `${id}`)
  );
  const features = featureCollection(fields);
  const { bbox } = envelope(features);
  const bboxQuery = `&bbox=${bbox.slice().reverse()}`;
  const sourcesQuery = "&sources=s2";
  const query = `${startDateQuery}${endDateQuery}${bboxQuery}${sourcesQuery}`;
  const { isLoading: isLoadingCatalog, data: catalogData } =
    useCatalogQuery(query);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const [rasterLoading, setRasterLoading] = useState(false);

  useEffect(() => {
    if (isLoadingFields || map) return;

    mapboxgl.accessToken = process.env.REACT_APP_MAPGL_ACCESS_TOKEN;
    const mapElement = mapContainer.current.getBoundingClientRect();
    const mapDimension = [mapElement.height, mapElement.width];
    const bounds = geoViewport.viewport(bbox, mapDimension, 0, 12.5, 512);
    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      zoom: bounds.zoom,
      center: bounds.center,
      interactive: false,
      fitBoundsOptions: { animate: false },
      attributionControl: false,
      transformRequest: (url) => {
        if (url.startsWith(process.env.REACT_APP_DATA_FARMING_API_ORIGIN)) {
          return {
            url,
            headers: {
              Authorization: `Bearer ${token}`,
              "df-api-key": `${process.env.REACT_APP_DF_API_KEY}`,
            },
          };
        }

        return null;
      },
    });
    const padding = {
      left: 50,
      top: 50,
      right: 50,
      bottom: 50,
    };
    newMap.fitBounds(bbox, {
      padding,
    });
    setMap(newMap);
  }, [bbox, isLoadingFields, map, token]);

  useEffect(() => {
    if (!map) return;

    map.on("load", () => {
      map.addLayer({
        id: "fields-outline",
        type: "line",
        source: {
          type: "geojson",
          data: fieldsData,
        },
        paint: {
          "line-color": [
            "case",
            ["boolean", ["feature-state", "active"], false],
            "#09BA51",
            "#FFFFFF",
          ],
          "line-width": [
            "case",
            ["boolean", ["feature-state", "active"], false],
            3,
            5,
          ],
          "line-opacity": 0.7,
        },
        filter: ["==", "$type", "Polygon"],
      });

      setMapFullyLoaded(true);
    });
  }, [fieldsData, map]);

  useEffect(() => {
    if (!mapFullyLoaded) return;

    const fieldsOutlineLayer = map.getLayer("fields-outline");
    if (!fieldsOutlineLayer) {
      return;
    }

    selectedFields.forEach((field) => {
      map.setFeatureState(
        { source: "fields-outline", id: field },
        { active: true }
      );
    });
  }, [map, mapFullyLoaded, selectedFields]);

  const updateRasterTile = (tileUrl) => {
    setRasterLoading(true);
    const coordinates = [
      [bbox[0], bbox[3]],
      [bbox[2], bbox[3]],
      [bbox[2], bbox[1]],
      [bbox[0], bbox[1]],
    ];
    const source = map.getSource("raster-tile-source");
    const layer = map.getLayer("raster-tile-layer");

    if (layer) {
      map.removeLayer("raster-tile-layer");
    }

    if (source) {
      map.removeSource("raster-tile-source");
    }

    map.addSource("raster-tile-source", {
      type: "image",
      url: tileUrl,
      coordinates,
    });
    map.addLayer(
      {
        id: "raster-tile-layer",
        type: "raster",
        source: "raster-tile-source",
      },
      "fields-outline"
    );

    map.on("idle", () => {
      setRasterLoading(false);
    });
  };

  const selectDate = (date, tileUrl) => {
    updateRasterTile(tileUrl);

    const otherYears = selectedDates.filter(
      (item) => item.year !== currentYear
    );
    const newSelectedDates = [
      ...otherYears,
      { year: currentYear, date, imagery: tileUrl },
    ];

    dispatch({
      type: StackAnalysisActions.SET_SELECTED_DATES,
      value: newSelectedDates,
    });

    if (date !== "Skipped") return;

    const currentYearIndex = selectedYears.indexOf(currentYear);

    if (currentYearIndex + 1 < selectedYears.length) {
      setCurrentYear(selectedYears[currentYearIndex + 1]);
    }
  };

  const yearClassName = (year) => {
    let className = "year";

    if (currentYear === year) {
      className += " -selected";
    }

    return className;
  };

  const monthClassName = (month) => {
    let className = "month";
    const selectedDate = selectedDates.find(
      (item) => item.year === currentYear
    ).date;

    if (selectedDate === month) {
      className += " -selected";
    }

    return className;
  };

  const renderDateValue = (year) => {
    const { date } = selectedDates.find((item) => item.year === year);

    if (date === null) {
      return "No date selected";
    }

    return date;
  };

  const renderCloudIndicator = (cloudPercentage) => {
    let icon = <Sun size={16} color="#4d515c" weight="bold" />;

    if (cloudPercentage >= 15 && cloudPercentage <= 59) {
      icon = <CloudSun size={16} color="#4d515c" weight="bold" />;
    }

    if (cloudPercentage >= 60) {
      icon = <Cloud size={16} color="#4d515c" weight="bold" />;
    }

    return (
      <div className="indicator">
        {cloudPercentage}%<span className="icon">{icon}</span>
      </div>
    );
  };

  const renderMonths = () => {
    if (isLoadingCatalog) {
      return (
        <div className="catalog-skeleton">
          <div className="skeleton-item">
            <div className="skeleton-dash -animate" />
            <div className="skeleton-dash -animate" />
          </div>
          <div className="skeleton-item">
            <div className="skeleton-dash -animate" />
            <div className="skeleton-dash -animate" />
          </div>
          <div className="skeleton-item">
            <div className="skeleton-dash -animate" />
            <div className="skeleton-dash -animate" />
          </div>
          <div className="skeleton-item">
            <div className="skeleton-dash -animate" />
            <div className="skeleton-dash -animate" />
          </div>
          <div className="skeleton-item">
            <div className="skeleton-dash -animate" />
            <div className="skeleton-dash -animate" />
          </div>
          <div className="skeleton-item">
            <div className="skeleton-dash -animate" />
            <div className="skeleton-dash -animate" />
          </div>
        </div>
      );
    }

    if (!catalogData?.length) {
      return <div className="placeholder">No dates available</div>;
    }

    return catalogData
      .sort((a, b) => new Date(a.attributes.time) - new Date(b.attributes.time))
      .map((data) => {
        return (
          <div
            onClick={() =>
              selectDate(
                `${selectedMonth} ${new Date(data.attributes.time).getDate()}`,
                data.links.trueColorImage
              )
            }
            className={monthClassName(
              `${selectedMonth} ${new Date(data.attributes.time).getDate()}`
            )}
            aria-hidden
            key={data.attributes.time}
          >
            <p className="date">
              {selectedMonth} {new Date(data.attributes.time).getDate()}
            </p>
            {renderCloudIndicator(Math.round(data.attributes.cloudCover))}
          </div>
        );
      });
  };

  return (
    <div className="stack-captures-selection">
      <div className="dates-wrapper">
        <div className="years-wrapper">
          <div className="list-options">
            <Stack size={32} color="#339DFF" className="icon" />
            <h2 className="title">Choose captures</h2>
          </div>
          <div className="years">
            {selectedYears.map((year) => (
              <div
                className={yearClassName(year)}
                onClick={() => setCurrentYear(year)}
                key={year}
                aria-hidden
              >
                <p className="display">{year}</p>
                <p className="selected-date">{renderDateValue(year)}</p>

                {selectedDates.find((item) => item.year === year).date !==
                  null && (
                  <div className="icon">
                    <CheckCircle size={20} color="#00CA52" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="page-action">
            <Button
              type="solid"
              label="Next"
              color="green"
              rounded
              block
              disabled={
                selectedDates.filter(
                  (item) => item.date !== null && item.date !== "Skipped"
                ).length < 2 || selectedDates.some((item) => item.date === null)
              }
              onClick={() =>
                dispatch({
                  type: StackAnalysisActions.SET_PAGE,
                  value: "review",
                })
              }
            />
            <Button
              label="Back"
              color="white"
              rounded
              type="solid"
              block
              onClick={() =>
                dispatch({
                  type: StackAnalysisActions.SET_PAGE,
                  value: "seasons",
                })
              }
            />
          </div>
        </div>
        <div className="months-wrapper">
          <div className="month-selector input-field">
            <select
              name="crop-types"
              className="input"
              defaultValue={selectedMonth}
              onChange={(event) => {
                dispatch({
                  type: StackAnalysisActions.SET_SELECTED_MONTH,
                  value: event.target.value,
                });
              }}
            >
              {months.map((month) => (
                <option value={month} key={month}>
                  {month}
                </option>
              ))}
            </select>
            <p className="label">Selected month</p>
          </div>
          <div className="months">{renderMonths()}</div>
          <div
            className="skip-year"
            onClick={() => selectDate("Skipped")}
            aria-hidden
          >
            Skip year
          </div>
        </div>
      </div>
      <div className="stack-map-wrapper">
        {rasterLoading && (
          <div className="raster-loading">
            <Loading />
          </div>
        )}
        <div className="map-container" ref={mapContainer} />
      </div>
    </div>
  );
};

export default StackCapturesSelection;
